import { useTranslation } from "react-i18next";
import { Search, Filter, ChevronDown, X, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { DateRangePicker } from "../ui/date-range-picker";

interface AttendanceSearchAndFilterProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
}

export const AttendanceSearchAndFilter = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  dateRange,
  onDateRangeChange,
}: AttendanceSearchAndFilterProps) => {
  const { t } = useTranslation("common");
  const isMobile = useIsMobile();

  // Status badge variant
  const getStatusBadgeVariant = (status: string): "default" | "destructive" | "secondary" | "outline" => {
    switch (status) {
      case "upcoming": return "default";
      case "completed": return "outline";
      case "cancelled": return "destructive";
      default: return "secondary";
    }
  };

  // Status display name
  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case "upcoming": return t("upcoming");
      case "completed": return t("completed");
      case "cancelled": return t("cancelled");
      default: return t("allStatuses");
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">

      {/* Search */}
      <div className="relative w-full sm:w-64">
        <Search className={`absolute left-2 top-1/2 -translate-y-1/2 ${isMobile ? "h-3 w-3" : "h-4 w-4"} text-muted-foreground`} />
        <Input
          placeholder={t("search")}
          className={`${isMobile ? "pl-7 pr-6 py-1 h-8 text-xs" : "pl-9 pr-8"}`}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {searchTerm && (
          <button
            onClick={() => onSearchChange("")}
            className={`absolute right-2 top-1/2 -translate-y-1/2 ${isMobile ? "h-3 w-3" : "h-4 w-4"} text-muted-foreground hover:text-foreground`}
          >
            <X className={`${isMobile ? "h-3 w-3" : "h-4 w-4"}`} />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-center gap-2">

        {/* Date Range Picker */}
        <div className="flex items-center gap-2">
        <DateRangePicker
            initialDateRange={dateRange}
            onUpdate={(range) => {
              if (range?.from && range?.to && range.from > range.to) {
                alert(t("invalidDateRange"));
                return;
              }
              onDateRangeChange(range);
            }}
          />

          {dateRange?.from && dateRange?.to && (
            <Badge variant="outline" className="flex items-center gap-1">
              {format(dateRange.from, "dd/MM/yyyy")} - {format(dateRange.to, "dd/MM/yyyy")}
              <button
                onClick={() => onDateRangeChange(undefined)}
                className="ml-1 hover:bg-background/20 rounded-full"
              >
                <X className={`${isMobile ? "h-2 w-2" : "h-3 w-3"}`} />
              </button>
            </Badge>
          )}
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-1">
          {statusFilter !== "all" && (
            <Badge
              variant={getStatusBadgeVariant(statusFilter)}
              className={`mr-1 ${isMobile ? "text-xs px-1 py-0" : ""}`}
            >
              {getStatusDisplayName(statusFilter)}
              <button
                className="ml-1 hover:bg-background/20 rounded-full"
                onClick={() => onStatusFilterChange("all")}
              >
                <X className={`${isMobile ? "h-2 w-2" : "h-3 w-3"}`} />
              </button>
            </Badge>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size={isMobile ? "xs" : "sm"}
                className={isMobile ? "h-7 text-xs px-2" : "h-9"}
                data-testid="attendance-filter-btn" // ← AJOUT ICI
              >
                <Filter className={`mr-1 ${isMobile ? "h-3 w-3" : "h-4 w-4"}`} />
                {isMobile ? "" : t("filterBy")}
                <ChevronDown className={`ml-1 ${isMobile ? "h-3 w-3" : "h-4 w-4"}`} />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end"
            data-testid="attendance-dropdown-content" className={`w-[150px] ${isMobile ? "text-xs" : "w-[200px]"}`}>
              <DropdownMenuItem data-testid="status-option-all" onClick={() => onStatusFilterChange("all")}>
                {t("allStatuses")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="status-option-upcoming" onClick={() => onStatusFilterChange("upcoming")}>
                {t("upcoming")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="status-option-completed" onClick={() => onStatusFilterChange("completed")}>
                {t("completed")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="status-option-cancelled" onClick={() => onStatusFilterChange("cancelled")}>
                {t("cancelled")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
